import React, { useState, useEffect } from "react";
import DataTable from "../../../components/DataTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserEntity } from "../../../services/users/user.types";
import { Alert, Button, App } from "antd";
import {
  useGetUsersPaginatedQuery,
  useDeleteUserMutation,
} from "../../../store/api/usersApi";
import TableSearchBar from "../../../components/TableSearchBar";
import useDebounce from "../../../hooks/useDebounce";
import { useClampPageToTotal } from "../../../hooks/useClampPageToTotal";
import DeleteUserConfirmModal from "../../../components/common/DeleteUserConfirmModal";
import PageHeader from "../../../components/PageHeader";
import ActivityIndicator from "../../../components/ActivityIndicator";
import { useIsInitialLoad } from "../../../hooks/useIsInitialLoad";

const DEFAULT_PAGE_SIZE = 30;

const Users: React.FC = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();

  // The URL is the source of truth for page, pageSize, id, email and q: the
  // list is deep-linkable and survives refreshes. Inputs keep their own
  // local state and sync into the URL after the debounce below.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") ?? "", 10) || 1;
  const pageSize =
    parseInt(searchParams.get("pageSize") ?? "", 10) || DEFAULT_PAGE_SIZE;
  const idParam = searchParams.get("id") ?? "";
  const emailParam = searchParams.get("email") ?? "";
  const qParam = searchParams.get("q") ?? "";

  const [searchId, setSearchId] = useState(idParam);
  const [searchEmail, setSearchEmail] = useState(emailParam);
  const [searchQ, setSearchQ] = useState(qParam);
  const [deleteModalUser, setDeleteModalUser] = useState<UserEntity | null>(
    null,
  );

  const debouncedSearchId = useDebounce(searchId, 500);
  const debouncedSearchEmail = useDebounce(searchEmail, 500);
  const debouncedSearchQ = useDebounce(searchQ, 500);

  // Debounced inputs → URL. Changing a filter always resets to page 1.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const currentId = prev.get("id") ?? "";
        const currentEmail = prev.get("email") ?? "";
        const currentQ = prev.get("q") ?? "";
        if (
          currentId === debouncedSearchId &&
          currentEmail === debouncedSearchEmail &&
          currentQ === debouncedSearchQ
        ) {
          return prev;
        }
        const next = new URLSearchParams(prev);
        if (debouncedSearchId) next.set("id", debouncedSearchId);
        else next.delete("id");
        if (debouncedSearchEmail) next.set("email", debouncedSearchEmail);
        else next.delete("email");
        if (debouncedSearchQ) next.set("q", debouncedSearchQ);
        else next.delete("q");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [
    debouncedSearchId,
    debouncedSearchEmail,
    debouncedSearchQ,
    setSearchParams,
  ]);

  // `data` (instead of `currentData`) keeps the previous page visible while
  // the next one is fetching, so the table doesn't flicker to empty.
  const { data, isFetching, isError, refetch } = useGetUsersPaginatedQuery({
    page,
    pageSize,
    id: /^\d+$/.test(idParam) ? parseInt(idParam, 10) : undefined,
    email: emailParam || undefined,
    q: qParam || undefined,
  });
  const [deleteUser] = useDeleteUserMutation();

  const users = data?.data ?? [];
  const pagination = data?.pagination ?? { page, pageSize, total: 0 };
  const isInitialLoad = useIsInitialLoad(isFetching, users);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      next.set("pageSize", String(newPageSize));
      return next;
    });
  };

  // Deleting the last item of the last page must not leave an empty table.
  useClampPageToTotal({
    total: pagination.total,
    pageSize,
    page,
    onPageChange: (lastPage) => handlePageChange(lastPage, pageSize),
  });

  const handleEdit = (record: UserEntity) => {
    navigate(`/users/${record.id}`);
  };

  const handleDelete = (record: UserEntity) => {
    setDeleteModalUser(record);
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalUser) return;
    try {
      await deleteUser(deleteModalUser.id).unwrap();
      notification["success"]({
        message: "Éxito",
        description: "El usuario fue eliminado exitosamente!",
        placement: "top",
        duration: 3,
      });
      // Solo cerramos el modal cuando efectivamente se borró; si falló, se
      // muestra la notificación de error y el modal permanece abierto para
      // que el usuario pueda reintentar o cancelar.
      setDeleteModalUser(null);
    } catch (error: any) {
      notification["error"]({
        message: "Eliminación Erronea",
        description:
          error?.data?.message || "El usuario no pudo ser eliminado!",
        placement: "top",
        duration: 3,
      });
    }
  };

  return (
    <div>
      <PageHeader
        title="Usuarios"
        extra={
          <TableSearchBar
            fields={[
              {
                placeholder: "Buscar por ID",
                value: searchId,
                onChange: setSearchId,
                width: 200,
              },
              {
                placeholder: "Buscar por Email",
                value: searchEmail,
                onChange: setSearchEmail,
                width: 200,
              },
              {
                placeholder: "Buscar por nombre o usuario",
                value: searchQ,
                onChange: setSearchQ,
                width: 240,
              },
            ]}
          >
            <Button type="primary" onClick={() => navigate("/users/new")}>
              Nuevo usuario
            </Button>
          </TableSearchBar>
        }
      />
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No se pudieron cargar los usuarios."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}
      {isInitialLoad ? (
        <ActivityIndicator />
      ) : (
        <DataTable
          data={users}
          columns={[
            { title: "ID", dataIndex: "id", key: "id", width: 80 },
            { title: "Nombre", dataIndex: "first_name", key: "first_name" },
            { title: "Apellido", dataIndex: "last_name", key: "last_name" },
            { title: "Email", dataIndex: "email", key: "email" },
            { title: "Usuario", dataIndex: "username", key: "username" },
            { title: "Rol", dataIndex: "role", key: "role" },
            { title: "Escuela", dataIndex: "school", key: "school" },
          ]}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={isFetching}
          skipDeleteConfirm
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: handlePageChange,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30"],
          }}
        />
      )}
      <DeleteUserConfirmModal
        visible={deleteModalUser !== null}
        userName={
          deleteModalUser
            ? `${deleteModalUser.first_name} ${deleteModalUser.last_name}`
            : ""
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalUser(null)}
      />
    </div>
  );
};

export default Users;
